import {
  CategorySet,
  CategorySetDefaultInventoryItem,
  InventoryItem,
} from '@app/types/shop-bff/types';
import { createContext, type ReactNode, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';

// FIXME: not sure about the type. Can we specific a type like "BlankItemId"?
// so that setBlankItemId return Dispatch<SetStateAction<BlankItemId>>?
type BlankItemId = InventoryItem['blankItemId'];

export const getCurrentInventoryItem = (
  defaultInventoryItem: CategorySetDefaultInventoryItem,
  inventoryItems: InventoryItem[],
  blankItemId: BlankItemId
) =>
  inventoryItems.find(({ blankItemId: b }) => b === blankItemId) ??
  defaultInventoryItem;

export type CurrentInventoryItemState = {
  currentInventoryItem: InventoryItem;
};

export type CurrentInventoryItemActions = {
  setCurrentInventoryItem: (item: InventoryItem) => void;
};

export type CurrentInventoryItemStore = CurrentInventoryItemState &
  CurrentInventoryItemActions;

export const createCurrentInventoryItemStore = (initialItem: InventoryItem) => {
  return createStore<CurrentInventoryItemStore>()((set) => ({
    currentInventoryItem: initialItem,
    setCurrentInventoryItem: (item) => set({ currentInventoryItem: item }),
  }));
};

export type CurrentInventoryStoreApi = ReturnType<
  typeof createCurrentInventoryItemStore
>;

export const CurrentInventoryStoreContext = createContext<
  CurrentInventoryStoreApi | undefined
>(undefined);

export interface CurrentInventoryStoreProviderProps {
  children: ReactNode;
  initialItem: InventoryItem;
  inventoryCategorySet: CategorySet;
}

export const CurrentInventoryStoreProvider = ({
  children,
  initialItem,
  inventoryCategorySet,
}: CurrentInventoryStoreProviderProps) => {
  const storeRef = useRef<CurrentInventoryStoreApi>();

  const currentInventoryItem =
    storeRef.current?.getState().currentInventoryItem;
  if (
    !storeRef.current ||
    initialItem.workId !==
      storeRef.current.getState().currentInventoryItem.workId
  ) {
    const item = getCurrentInventoryItem(
      inventoryCategorySet.defaultItem,
      inventoryCategorySet.items,
      currentInventoryItem?.blankItemId || initialItem.blankItemId
    );
    storeRef.current = createCurrentInventoryItemStore(item);
  }

  return (
    <CurrentInventoryStoreContext.Provider value={storeRef.current}>
      {children}
    </CurrentInventoryStoreContext.Provider>
  );
};

export const useCurrentInventoryStore = <T,>(
  selector: (store: CurrentInventoryItemStore) => T
): T => {
  const currentInventoryStoreContext = useContext(CurrentInventoryStoreContext);

  if (!currentInventoryStoreContext) {
    throw new Error(
      `useCurrentInventoryStore must be used within CurrentInventoryStoreProvider`
    );
  }

  return useStore(currentInventoryStoreContext, selector);
};
